import { AxiosError } from 'axios';
import { UploadFilesResponse } from '../hooks/useUploadFiles';
import axiosInstance from '../config/axios';
import { compressImageFiles } from '../utils/imageCompression';
import * as Sentry from "@sentry/react";

// Uploads over flaky mobile connections can be interrupted, leaving the server
// with a truncated multipart body (HTTP 400 "incomplete upload"). Those are
// transient, so retry. Don't retry permanent failures: auth (401/403) or
// payload-too-large (413).
const MAX_ATTEMPTS = 3;
const NON_RETRYABLE_STATUSES = [401, 403, 413];

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class FileService {

    static async uploadFiles(
        files: File[],
        onProgress?: (loaded: number, total: number) => void
    ): Promise<UploadFilesResponse> {
        try {
            console.log('[uploadFiles] incoming files:', files.length,
                files.map((f) => ({ name: f.name, type: f.type, size: f.size })));

            if (!files.length) {
                // Nothing to send — surface it clearly instead of hitting the API
                // with an empty body and getting a confusing "No files were uploaded".
                throw new Error('uploadFiles called with no files');
            }

            // Compress/re-encode images before upload. This shrinks the payload
            // (shorter upload window = fewer interrupted uploads) and, crucially,
            // rebuilds each image into a fresh in-memory blob with known byte
            // length — avoiding truncated multipart bodies from iOS/iCloud
            // "optimized" photos whose reported size doesn't match readable bytes.
            // Idempotent: already-small images are returned untouched.
            const prepared = await compressImageFiles(files);
            console.log('[uploadFiles] prepared files:', prepared.length,
                prepared.map((f) => ({ name: f.name, type: f.type, size: f.size })));

            let lastError: unknown;
            for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
                // Build a fresh FormData each attempt (a consumed body can't be resent).
                const formData = new FormData();
                prepared.forEach((file) => {
                    formData.append('files', file);
                });

                try {
                    // IMPORTANT: axiosInstance has a default `Content-Type: application/json`.
                    // In axios 1.x, posting FormData with a JSON content type serializes the
                    // body to JSON and DROPS the file parts, causing the API to reply
                    // "No files were uploaded." Override to multipart/form-data (same as the
                    // working category upload) so axios sends the real multipart body with a
                    // browser-generated boundary.
                    console.log(`[uploadFiles] POST /file/upload attempt ${attempt}/${MAX_ATTEMPTS}, parts:`, prepared.length);
                    const response = await axiosInstance.post<UploadFilesResponse>(
                        '/file/upload',
                        formData,
                        {
                            headers: { 'Content-Type': 'multipart/form-data' },
                            onUploadProgress: onProgress
                                ? (e) => onProgress(e.loaded, e.total ?? 0)
                                : undefined,
                        }
                    );
                    console.log('[uploadFiles] success:', response.data);
                    return response.data;
                } catch (error) {
                    lastError = error;
                    const status = (error as AxiosError)?.response?.status;
                    const responseData = (error as AxiosError)?.response?.data;
                    console.error(`[uploadFiles] attempt ${attempt} failed. status:`, status, 'response:', responseData);
                    const isPermanent = status != null && NON_RETRYABLE_STATUSES.includes(status);
                    if (isPermanent || attempt === MAX_ATTEMPTS) throw error;

                    await delay(Math.min(1000 * 2 ** (attempt - 1), 8000)); // 1s, 2s, capped
                }
            }
            // Unreachable — the loop either returns or throws — but satisfies the type checker.
            throw lastError;
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error uploading files:', error);
            throw error; // Add this line to rethrow the error
        }
    }

}